/**
 * **The offline day, held by the station** (7g, F3): the queue and since when, the day kept here —
 * read at Open the booth and every five minutes online — and the seal, in order, when the network
 * is back. `onBack` says how the seal went and reads the day again. The storage is `boothOffline.ts`.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { api, isRefused } from '../../shell/api.js';
import {
  loadDay,
  loadQueue,
  loadSince,
  saveDay,
  saveQueue,
  saveSince,
  type DayCache,
  type QueuedItem,
} from './boothOffline.js';

export interface BoothOffline {
  queue: QueuedItem[];
  offlineSince: string | null;
  offline: boolean;
  /** The network is gone (a refusal with no status): the station is offline from now, and says so. */
  goneOffline: (e: unknown) => boolean;
  keepDay: () => void;
  /** Queue one action with its real time, and move the day kept here to match. */
  enqueue: (item: Omit<QueuedItem, 'id' | 'at'>) => QueuedItem;
  /** The day kept here, changed by hand (a tag read against it offline). */
  setDayCache: (d: DayCache) => void;
}

export function useBoothOffline(onBack: (says: string) => void): BoothOffline {
  // 7g: the offline day — the day kept on the station, the queue, and since when.
  const [, setDayCache] = useState<DayCache | null>(() => loadDay());
  const [queue, setQueue] = useState<QueuedItem[]>(() => loadQueue());
  const [offlineSince, setOfflineSince] = useState<string | null>(() => loadSince());
  const offline = offlineSince !== null;
  const goneOffline = useCallback((e: unknown): boolean => {
    if (!(isRefused(e) && e.refusal.status === 0)) return false;
    setOfflineSince((was) => {
      const at = was ?? new Date().toISOString();
      saveSince(at);
      return at;
    });
    return true;
  }, []);
  const back = useRef(onBack);
  useEffect(() => {
    back.current = onBack;
  });

  /** The day the station keeps (7g): read at Open the booth and every five minutes online. */
  const keepDay = useCallback((): void => {
    api<DayCache>('GET', '/volunteers/booth/offline-day')
      .then((d) => {
        saveDay(d);
        setDayCache(d);
      })
      .catch(() => undefined);
  }, []);
  useEffect(() => {
    keepDay();
    const t = setInterval(keepDay, 5 * 60_000);
    return () => {
      clearInterval(t);
    };
  }, [keepDay]);

  /** Seal the queue in the order it was made (7g); what cannot seal goes to NEEDS WORK. */
  const syncing = useRef(false);
  const sync = useCallback((): void => {
    const items = loadQueue();
    if (syncing.current) return;
    if (items.length === 0) {
      setOfflineSince(null);
      saveSince(null);
      return;
    }
    syncing.current = true;
    api<{ results: { id: string; outcome: 'sealed' | 'needs work'; says: string }[] }>(
      'POST',
      '/volunteers/booth/sync',
      { items },
    )
      .then((r) => {
        const done = new Set(r.results.map((x) => x.id));
        const left = loadQueue().filter((x) => !done.has(x.id));
        saveQueue(left);
        setQueue(left);
        const work = r.results.filter((x) => x.outcome === 'needs work').length;
        const says = `Back online · ${String(r.results.length - work)} sealed at their own times${work ? ` · ${String(work)} on NEEDS WORK for the volunteer team` : ''}.`;
        if (left.length === 0) {
          setOfflineSince(null);
          saveSince(null);
        }
        back.current(says);
        keepDay();
      })
      .catch((e: unknown) => {
        goneOffline(e);
      })
      .finally(() => {
        syncing.current = false;
      });
  }, [goneOffline, keepDay]);

  // While offline, one small read every fifteen seconds asks whether the network is back.
  useEffect(() => {
    if (!offline) return;
    const t = setInterval(() => {
      api('GET', '/volunteers/booth/desk')
        .then(() => {
          sync();
        })
        .catch(() => undefined);
    }, 15_000);
    window.addEventListener('online', sync);
    return () => {
      clearInterval(t);
      window.removeEventListener('online', sync);
    };
  }, [offline, sync]);

  /** Queue one action with its real time (7g), and move the day the station keeps to match. */
  const enqueue = (item: Omit<QueuedItem, 'id' | 'at'>): QueuedItem => {
    const full: QueuedItem = { ...item, id: crypto.randomUUID(), at: new Date().toISOString() };
    const next = [...loadQueue(), full];
    saveQueue(next);
    setQueue(next);
    const d = loadDay();
    if (d && full.personId && full.shiftId) {
      const state =
        full.kind === 'check-in'
          ? 'in'
          : full.kind === 'check-out'
            ? 'out'
            : full.kind === 'undo'
              ? 'expected'
              : null;
      if (state)
        for (const p of d.people)
          if (p.personId === full.personId)
            for (const t of p.today) if (t.shiftId === full.shiftId) t.state = state;
      if (full.tag)
        d.tags.push({
          code: full.tag,
          personId: full.personId,
          shiftId: full.kind === 'reassign' ? (full.toShiftId ?? full.shiftId) : full.shiftId,
          inAt: full.at,
        });
      saveDay(d);
      setDayCache(d);
    }
    return full;
  };

  return { queue, offlineSince, offline, goneOffline, keepDay, enqueue, setDayCache };
}
